import { useState } from "react";
import { Button, TextField } from "@legends/ui";
import { login, resendVerification, ssoStartURL, ApiError, type AuthProvider, type AuthUser } from "./api";
import { currentReturnPath } from "./checkoutReturn";
import { LegendBrandMark } from "./sections";

// Unicode glyphs only (Meridian: no icon fonts, no SVG icon sets).
const PROVIDER_GLYPH: Record<string, string> = {
  google: "◎",
  microsoft: "▦",
  twitter: "◫",
  apple: "◆",
};

export interface LoginScreenProps {
  providers: AuthProvider[];
  onLogin: (user: AuthUser) => void;
  /** SSO error hint bounced back from a failed provider round-trip (?sso_error=…). */
  ssoError?: string;
  /**
   * The exact ticket the Fan was about to buy when bounced here to sign in
   * (ADR 0103). Carried into the SSO start so the purchase — not just the page —
   * resumes on return.
   */
  buyTicketTypeId?: string;
  /** Open the local sign-up page (LSC-554). Shown as a "Create an account" link. */
  onCreateAccount?: () => void;
  /** Open the forgot-password page (LSC-614). Shown as a "Forgot password?" link. */
  onForgotPassword?: () => void;
}

// The Meridian sign-in console: local credentials + one social button per
// CONFIGURED provider. Unconfigured providers are not shown at all — we never
// render UI for something that doesn't work (CLAUDE.md; ADR 0103).
export default function LoginScreen({ providers, onLogin, ssoError, buyTicketTypeId, onCreateAccount, onForgotPassword }: LoginScreenProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(ssoError ? `SSO sign-in failed: ${ssoError}` : null);
  const [busy, setBusy] = useState(false);
  // When a local login is refused as unverified (403, LSC-554), offer a one-click
  // resend of the verification email to the address they just tried.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  // Only CONFIGURED providers get a button — never a dead one (ADR 0103).
  const enabledProviders = providers.filter((p) => p.enabled);

  const submit = () => {
    if (busy || !username || !password) return;
    setBusy(true);
    setError(null);
    setUnverifiedEmail(null);
    setResent(false);
    login(username, password)
      .then(onLogin)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 403) {
          // Local account exists but email isn't verified — surface the resend path.
          setUnverifiedEmail(username.trim());
        }
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setBusy(false));
  };

  const resend = () => {
    if (!unverifiedEmail) return;
    setResent(false);
    resendVerification(unverifiedEmail)
      .then(() => setResent(true))
      .catch(() => setResent(true));
  };

  return (
    <main
      className="mer-canvas mer-canvas--photo"
      style={{ minHeight: "calc(100vh / var(--ui-zoom, 1))", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          padding: "26px 32px 24px",
          width: 360,
          animation: "cnPop var(--dur) var(--ease-out)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <LegendBrandMark />
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-2xs)",
              fontWeight: 600,
              letterSpacing: "var(--ls-caps)",
              textTransform: "uppercase",
              color: "var(--text-label)",
            }}
          >
            Legends · Sign in
          </h1>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          style={{ marginTop: 20, display: "grid", gap: 14 }}
        >
          <TextField
            label="Username"
            name="username"
            value={username}
            onChange={setUsername}
            autoFocus
            full
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            value={password}
            onChange={setPassword}
            onSubmit={submit}
            full
          />
          {error && (
            <p role="alert" style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--alert-2)" }}>
              {error}
            </p>
          )}
          {unverifiedEmail && (
            <Button full type="button" onClick={resend} disabled={resent}>
              {resent ? "Verification email re-sent" : "Resend verification email"}
            </Button>
          )}
          <Button variant="accent" full disabled={busy || !username || !password} type="submit">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        {enabledProviders.length > 0 && (
          <>
            <div
              aria-hidden
              style={{
                margin: "20px 0 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "var(--text-dim)",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-3xs)",
                letterSpacing: "var(--ls-label-wide)",
                textTransform: "uppercase",
              }}
            >
              <span style={{ flex: 1, borderTop: "1px solid var(--border-subtle)" }} />
              or continue with
              <span style={{ flex: 1, borderTop: "1px solid var(--border-subtle)" }} />
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {enabledProviders.map((p) => (
                <Button
                  key={p.id}
                  full
                  onClick={() => {
                    // Preserve the cave/legend/buy page the Fan is on — and the exact
                    // ticket they were about to buy — across the OAuth hop so a
                    // first-time Fan returns to continue the purchase (ADR 0066/0103).
                    window.location.href = ssoStartURL(p.id, currentReturnPath(), buyTicketTypeId);
                  }}
                >
                  <span aria-hidden style={{ marginRight: 8, color: "var(--accent)" }}>
                    {PROVIDER_GLYPH[p.id] ?? "◇"}
                  </span>
                  Continue with {p.name}
                </Button>
              ))}
            </div>
          </>
        )}

        {onForgotPassword && (
          <button
            type="button"
            onClick={onForgotPassword}
            style={{
              marginTop: 14,
              background: "none",
              border: "none",
              color: "var(--accent)",
              cursor: "pointer",
              fontSize: "var(--fs-sm)",
              width: "100%",
            }}
          >
            Forgot password?
          </button>
        )}

        {onCreateAccount && (
          <button
            type="button"
            onClick={onCreateAccount}
            style={{
              marginTop: 8,
              background: "none",
              border: "none",
              color: "var(--accent)",
              cursor: "pointer",
              fontSize: "var(--fs-sm)",
              width: "100%",
            }}
          >
            New here? Create an account
          </button>
        )}
      </div>
    </main>
  );
}
